/**
 * Toolbox ▸ SDMS Template Builder — the full-page template editor (port of the
 * Alpaca Studio TemplateBuildPage).
 *
 * The template's .xlsx is EDITED in the browser (TemplateSheetDesigner): text,
 * formulas, font / fill / borders / alignment / number format, merges, column and row
 * size through Univer's own toolbar; Scientific Data fields are dragged from the field
 * list (or picked with Map Field) onto a cell, which writes `=FIELD("NAME")` and maps
 * the field. The mail-merge Header / Detail bands, report columns and conditional
 * formats are set from the selection; the tabs hold everything else Result Entry reads
 * (linked tests, grid columns, flags).
 *
 * Nothing is written until Save. The sheet is then diffed against how it was loaded
 * and ONLY the changes go to the server, which patches just those parts of the .xlsx
 * (byte-preserving the rest — legacy DevExpress opens the same file) and reconciles
 * the tables in one transaction — see SdmsTemplateBuilderController.
 *
 * Bespoke page (not <SettingsDetailPage>): the shared detail has no slot for a
 * spreadsheet canvas — same precedent as SdmsSequenceSetupPage / DailyQcBuilderPage.
 * It still composes the shared toolbar, grid, dialogs, save/delete flows, hotkeys
 * and unsaved guard.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppToolbar, FormMetaChips } from '../../../components/AppToolbar';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ComboSelect } from '../../../components/ComboSelect';
import { api } from '../../../api/client';
import { FormTitle } from '../../../components/FormTitle';
import { useConfirm, useDeleteFlow, useSaveFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { formatCombo, useHotkeys } from '../../../components/hotkeys';
import { confirmDiscardIfUnsaved, registerUnsavedGuard, resetUnsavedTracking, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { useNavPermission } from '../../../hooks/useNavPermission';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { FIELD_DRAG_MIME, type ConditionalFormatCommand, type TemplateRibbonAction, type TemplateSheetHandle } from './TemplateSheetDesigner';
import { EMPTY_EDITS, isEmptyEdits, type SheetEdits } from './sheetEdits';
import { syncMappingFromEdits } from './mappingSync';
import { ConditionalFormatManager } from './ConditionalFormatManager';
import { DataTransferReportingTab, type DataTransferReportingHandle } from './DataTransferReportingTab';
import { rulesFromWorkbook, toEdits, type CellBox, type CfRule } from './cfModel';
import { dvRulesFromWorkbook, toDvEdits, type DvRule } from './dvModel';
import { DataValidationManager } from './DataValidationManager';
import {
  errorText, sdmsTemplateApi, toBase64,
  type BandChange, type BandName, type FieldOption, type Lookups, type TemplateDetail, type TemplateDetailMap,
  type TemplateField, type TemplateHeader, type TemplateHeaderMap, type TemplateParsing,
} from './sdmsTemplateApi';
import {
  a1, BAND_LABEL, BAND_NAMES, colIndex, colName, effectiveBands, fieldFormula, inRange,
  readTemplateWorkbook, sheetOf, type WorkbookInfo,
} from './templateWorkbook';
import { reviewTemplate } from './templateReview';
import { FORM_NAME, MODELS, PAGE_ID } from './templateConstants';
import { FieldPickerDialog, LinkTestDialog, type LinkedTestPick, LegacyTestDialog, LegacyFieldNameDialog } from './templateDialogs';
import '../../analysis/sdmsEntry.css';
import '../../modules/SettingsDetailPage.css';
import './sdmsTemplateBuilder.css';

// Lazy: Univer + the xlsx mapper only load when a template with a workbook is opened.
const TemplateSheetDesigner = lazy(() => import('./TemplateSheetDesigner').then(m => ({ default: m.TemplateSheetDesigner })));

type Tab = 'general' | 'tests' | 'fields' | 'mapping' | 'outputs' | 'bands' | 'review' | 'sheet' | 'mailmerge' | 'datasource';
/** Legacy builder order: General Info · Field Info · Parsing Data · Data Transfer And Reporting · DataSource Mapping. */
const TABS: { id: Tab; label: string }[] = [
  { id: 'general', label: 'General Info' },
  { id: 'tests', label: 'Linked Tests' },
  { id: 'fields', label: 'Field Info' },
  { id: 'mapping', label: 'Data Parsing' },
  { id: 'outputs', label: 'Data Transfer & Reporting' },
  { id: 'bands', label: 'Band and Data Source Mapping' },
  { id: 'review', label: 'Review' },
];
/**
 * The legacy ALPACA (web) builder's SEVEN tabs, in its order — opt-in via `legacyTabs`, used by
 * Settings ▸ SDMS ▸ SDMS Template Builder. Same editor, same data; only the tab strip differs:
 * SDMS = the sheet at full height; the linked tests sit on General Info (where legacy has its Test
 * field); Mail Merge and Data Source Mapping are the two halves of the Band and Data Source tab.
 */
/** Data Model as the legacy detail view captions it (EnumModel display names). SampleQCSplit (4)
 *  is not offered: this editor and SDMS Result Entry do not implement that model. */
const LEGACY_MODELS = [
  { id: 0, label: 'BatchSampleEntry' },
  { id: 1, label: 'SampleNavigation' },
  { id: 2, label: 'CalibrationSet' },
  { id: 3, label: 'FieldSampleCollection' },
] as const;
const FI_AVAILABLE_COLS: AppColumn<FieldOption>[] = [{ field: 'fieldName', header: 'FieldName', width: 190 }];
/** Mail Merge tab radio groups — the index is the stored value (legacy EnumMailMergeMode / EnumOrientation). */
const MAIL_MERGE_MODES = ['Single sheet', 'Multiple sheets', 'Multiple documents'] as const;
const DOCUMENT_ORIENTATIONS = ['Landscape', 'Vertical'] as const;
/** Data Source Mapping inner tabs (legacy order). */
const DS_TABS = ['Header', 'Detail', 'CHeader', 'CDetail'] as const;
const NO_ROWS: TemplateHeaderMap[] = [];
const NO_DETAIL_COLS: AppColumn<TemplateHeaderMap>[] = [
  { field: 'caption', header: 'ColumnMapping', width: 520 },
  { field: 'position', header: 'ColumnIndex', width: 320 },
];
const LEGACY_TABS: { id: Tab; label: string }[] = [
  { id: 'general', label: 'General Info' },
  { id: 'sheet', label: 'SDMS' },
  { id: 'fields', label: 'Field Info' },
  { id: 'mapping', label: 'Parsing Data' },
  { id: 'outputs', label: 'Data Transfer' },
  { id: 'mailmerge', label: 'Mail Merge' },
  { id: 'datasource', label: 'Data Source Mapping' },
];

const EMPTY_INFO: WorkbookInfo = { sheets: [], bands: [], formulas: [], conditionalFormats: [] };

const TRACEABILITY = ['Parameter-Oriented Summary', 'Sample-Oriented Summary', 'Reagents', 'Instruments'] as const;
const YES_NO = ['Yes', 'No'];
const yn = (b: boolean) => (b ? 'Yes' : 'No');

const blankHeader = (): TemplateHeader => ({
  templateID: 0, templateName: '', templateTypeId: null, dtSheetID: 1, isActive: true, isRetire: false,
  isSampling: false, isSampleBased: false, orientation: false, calibrationLevelNo: 0, model: 0, source: null,
  sampleOrientedSummary: false, parameterOrientedSummary: false, traceability: null,
});
const blankTemplate = (): TemplateDetail => ({ header: blankHeader(), tests: [], fields: [], parsing: [], headers: [], details: [] });

/** New rows carry negative temp ids until saved (the server inserts any id ≤ 0). */
/** Tab-panel height (px) — remembered per browser; the sheet gets the rest of the window. */
const PANEL_DEFAULT_H = 210;
const PANEL_MIN_H = 110;
const PANEL_MAX_H = 640;
const SHEET_MIN_H = 300;
const PANEL_H_KEY = 'ai.sdmsTemplateBuilder.panelHeight';
const PANEL_OPEN_KEY = 'ai.sdmsTemplateBuilder.panelOpen';
const FIELDLIST_DOCK_KEY = 'ai.sdmsTemplateBuilder.fieldListDocked';
const clampPanel = (h: number) => Math.min(PANEL_MAX_H, Math.max(PANEL_MIN_H, Math.round(h)));
// A layout preference only — storage can be unavailable (private window, blocked site data).
function readPref(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function writePref(key: string, value: string) {
  try { window.localStorage.setItem(key, value); } catch { /* preference only — the layout still works */ }
}

let tempSeq = -1;
const tempId = () => tempSeq--;

/** Rows as the API expects them: temp ids → 0. */
function forSave(t: TemplateDetail): TemplateDetail {
  const tl = new Set((t.header.traceability ?? '').split(',').map(s => s.trim()).filter(Boolean));
  return {
    ...t,
    header: {
      ...t.header,
      templateName: t.header.templateName.trim(),
      // Keep the legacy summary flags in step with the Traceability list they duplicate.
      sampleOrientedSummary: tl.has('Sample-Oriented Summary'),
      parameterOrientedSummary: tl.has('Parameter-Oriented Summary'),
    },
    fields: t.fields.map(f => ({ ...f, uqID: Math.max(0, f.uqID) })),
    parsing: t.parsing.map(p => ({ ...p, uqID: Math.max(0, p.uqID) })),
    headers: t.headers.map(h => ({ ...h, uqID: Math.max(0, h.uqID) })),
    details: t.details.map(d => ({ ...d, uqID: Math.max(0, d.uqID) })),
  };
}

interface GroupedTest { testMethodID: string; testName: string; methodName: string; matrixName: string; parameters: string }

export default function SdmsTemplateEditor({ id, lookups, onClose, onSaved, onHome, onOpen, legacyTabs = false, initialMode = 'edit' }: {
  id: number | null;
  lookups: Lookups | null;
  onClose: () => void;
  onSaved: () => void;
  onHome?: () => void;
  /** open another template (after Save As / create) */
  onOpen: (id: number) => void;
  /** Show the legacy builder's seven tabs (`LEGACY_TABS`) and the Settings ▸ SDMS breadcrumb.
   *  Default false — the Toolbox editor is unchanged. */
  legacyTabs?: boolean;
  /** How an EXISTING template opens: 'view' = read-only with an Edit button (a row click on the
   *  Settings list); 'edit' (default) = editable straight away. A new template is always editable.
   *  Default 'edit' — the Toolbox editor is unchanged. */
  initialMode?: 'view' | 'edit';
}) {
  const notify = useNotify();
  const { prompt, alert, confirm } = useConfirm();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const perm = useNavPermission('SDMS');
  // View → Edit (owner request 2026-10-13). While viewing, the editor behaves exactly as it does for
  // a user without Write permission — every existing read-only guard keys off `canWrite` — and the
  // Edit button lifts it. A template that has no id yet has nothing to view.
  const [mode, setMode] = useState<'view' | 'edit'>(id ? initialMode : 'edit');
  const viewing = mode === 'view' && !!id;
  const canWrite = perm.canWrite && !viewing;

  const [saved, setSaved] = useState<TemplateDetail>(blankTemplate);
  const [draft, setDraft] = useState<TemplateDetail>(blankTemplate);
  const [bands, setBands] = useState<BandChange[]>([]);
  const [upload, setUpload] = useState<{ buf: ArrayBuffer; name: string } | null>(null);
  const [storedBook, setStoredBook] = useState<ArrayBuffer | null>(null);
  /** True when `storedBook` is the blank starting sheet, not a saved workbook. */
  const [blankStart, setBlankStart] = useState(false);
  const [info, setInfo] = useState<WorkbookInfo>(EMPTY_INFO);
  /** Conditional-format rules: as the workbook holds them, and as edited in the Rules Manager (written on Save). */
  const [cfOriginal, setCfOriginal] = useState<CfRule[]>([]);
  const [cfRules, setCfRules] = useState<CfRule[]>([]);
  const [cfManager, setCfManager] = useState<{ newRuleOnly: boolean; selection: ({ sheetIndex: number } & CellBox) | null } | null>(null);
  const cfEdits = useMemo(() => toEdits(cfOriginal, cfRules), [cfOriginal, cfRules]);
  // Data validation (2026-10-12) — same shape as the conditional formats above.
  const [dvOriginal, setDvOriginal] = useState<DvRule[]>([]);
  const [dvRules, setDvRules] = useState<DvRule[]>([]);
  const [dvManager, setDvManager] = useState<{ newRuleOnly: boolean; selection: ({ sheetIndex: number } & CellBox) | null } | null>(null);
  const dvEdits = useMemo(() => toDvEdits(dvOriginal, dvRules), [dvOriginal, dvRules]);
  const [fieldSearch, setFieldSearch] = useState('');
  const [tab, setTab] = useState<Tab>('general');
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [sheetReady, setSheetReady] = useState(false);
  const [picker, setPicker] = useState<null | 'map' | 'fields'>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [testsOpen, setTestsOpen] = useState(false);
  // Legacy General Info: Entered By / Modified By are pickers over the users (as G5).
  const [employees, setEmployees] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    if (!legacyTabs) return;
    api.get<{ data?: { id: string; name: string }[] }>('/api/v1/collectors/employees')
      .then(r => setEmployees((r.data?.data ?? []).map(x => ({ value: String(x.id).toLowerCase(), label: x.name ?? '' }))))
      .catch(e => notify.error(errorText(e, 'Could not load the users.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyTabs]);
  const [selTests, setSelTests] = useState<Set<string | number>>(new Set());
  const [selMaps, setSelMaps] = useState<Set<string | number>>(new Set());
  const [selFields, setSelFields] = useState<Set<string | number>>(new Set());
  const [selHeaders, setSelHeaders] = useState<Set<string | number>>(new Set());
  const [selDetails, setSelDetails] = useState<Set<string | number>>(new Set());
  const [bandSheet, setBandSheet] = useState(1);
  const sheetRef = useRef<TemplateSheetHandle | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  /** Data Transfer & Reporting: its added / edited rows are stored with this Save (legacy parity). */
  const outputsRef = useRef<DataTransferReportingHandle | null>(null);
  const [outputsDirty, setOutputsDirty] = useState(false);

  const book = upload?.buf ?? storedBook;
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(saved) || bands.length > 0 || upload != null || cfEdits.length > 0 || dvEdits.length > 0 || outputsDirty,
    [draft, saved, bands, upload, cfEdits, dvEdits, outputsDirty]);
  useUnsavedGuard(dirty);
  /** The sheet's own edits are only known by diffing it — ask it when leaving. */
  const sheetEdits = (): SheetEdits => (sheetReady && sheetRef.current?.getEdits()) || EMPTY_EDITS;
  const sheetDirty = () => !isEmptyEdits(sheetEdits());
  useEffect(() => registerUnsavedGuard(sheetDirty));
  const anyDirty = () => dirty || sheetDirty();

  // ── load ────────────────────────────────────────────────────────────────
  const load = useCallback(async (templateId: number | null) => {
    const t0 = performance.now();
    setLoading(true);
    setSheetReady(false);
    try {
      // New (or a template that has no workbook yet) opens on an EMPTY sheet, like the Windows
      // builder — the server applies the edits onto the same blank package on Save.
      if (templateId) {
        const [detail, wb] = await Promise.all([sdmsTemplateApi.get(templateId), sdmsTemplateApi.workbook(templateId)]);
        const buf = wb ?? await sdmsTemplateApi.blankWorkbook();
        setSaved(detail);
        setDraft(detail);
        setStoredBook(buf);
        setBlankStart(!wb);
        setInfo(await readTemplateWorkbook(buf));
      } else {
        const t = blankTemplate();
        const buf = await sdmsTemplateApi.blankWorkbook();
        setSaved(t);
        setDraft(t);
        setStoredBook(buf);
        setBlankStart(true);
        setInfo(await readTemplateWorkbook(buf));
      }
      setBands([]);
      setUpload(null);
      setLoadMs(Math.round(performance.now() - t0));
      resetUnsavedTracking();
    } catch (e) {
      notify.error(errorText(e, 'Could not load the template.'));
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(id); }, [id, load]);

  // The uploaded workbook's own sheets / bands / formulas drive the editor once chosen.
  useEffect(() => {
    if (!upload) return;
    let alive = true;
    void readTemplateWorkbook(upload.buf).then(i => { if (alive) setInfo(i); });
    return () => { alive = false; };
  }, [upload]);

  // The rules in force are whatever the loaded / uploaded workbook holds.
  useEffect(() => {
    const r = rulesFromWorkbook(info.conditionalFormats);
    setCfOriginal(r);
    setCfRules(r);
    const d = dvRulesFromWorkbook(info.dataValidations);
    setDvOriginal(d);
    setDvRules(d);
  }, [info]);

  const leave = (go: () => void) => {
    void confirmDiscardIfUnsaved().then(ok => { if (ok) { resetUnsavedTracking(); go(); } });
  };

  // ── draft helpers ───────────────────────────────────────────────────────
  const setHeader = (patch: Partial<TemplateHeader>) => setDraft(d => ({ ...d, header: { ...d.header, ...patch } }));
  const allBands = useMemo(() => effectiveBands(info, bands), [info, bands]);
  const detailBandOf = (sheet: number) => allBands.find(b => b.sheetIndex === sheet && b.name === 'DETAILRANGE')?.range;
  const sheetName = (i: number) => info.sheets[i - 1] ?? `Sheet${i}`;

  /** The selection on the sheet, or a warning when there is none. */
  const selection = () => {
    const r = sheetRef.current?.getActiveRange();
    if (!r) { void alert({ title: FORM_NAME, message: 'Select a cell or range on the sheet first.', tone: 'warning' }); return null; }
    return r;
  };

  /** Maps a catalog field to the selected cell. */
  const mapField = (field: FieldOption) => {
    const r = selection();
    if (r) mapFieldAt(field, { sheetIndex: r.sheetIndex, row: r.startRow, col: r.startCol });
  };

  /** Maps a catalog field to a cell (replacing whatever that cell was mapped to) and writes
   *  `=FIELD("NAME")` into the design sheet, so the sheet and the mapping agree immediately. */
  const mapFieldAt = (field: FieldOption, at: { sheetIndex: number; row: number; col: number }) => {
    if (!field.fieldName) return;
    const pos = a1(at.row, at.col);
    const formula = fieldFormula(field.fieldName);
    const continuous = inRange(pos, detailBandOf(at.sheetIndex));
    sheetRef.current?.setCellFormula(at.sheetIndex, at.row, at.col, formula);
    setDraft(d => {
      const existing = d.parsing.find(p => sheetOf(p) === at.sheetIndex && (p.position ?? '').toUpperCase() === pos);
      const row: TemplateParsing = {
        uqID: existing?.uqID ?? tempId(), sheetID: at.sheetIndex, fieldID: field.id, fieldName: field.fieldName,
        resultType: field.resultType, position: pos, formula, format: existing?.format ?? null, continuous,
        read: existing?.read ?? true, write: existing?.write ?? true, caption: field.synonymEN || field.fieldName,
        defaultValue: existing?.defaultValue ?? null, usersFromDepartmentChain: existing?.usersFromDepartmentChain ?? false,
      };
      const parsing = existing ? d.parsing.map(p => (p === existing ? row : p)) : [...d.parsing, row];
      // A mapped field also becomes a batch-grid column (Studio parity: dropping a field adds it to Field Info).
      const fields = d.fields.some(f => f.fieldID === field.id) ? d.fields : [...d.fields, newFieldRow(field, d.fields)];
      return { ...d, parsing, fields };
    });
    setPicker(null);
    notify.info(`${field.fieldName} mapped to ${sheetName(at.sheetIndex)}!${pos}.`);
  };

  const newFieldRow = (f: FieldOption, existing: TemplateField[]): TemplateField => ({
    uqID: tempId(), fieldID: f.id, fieldName: f.fieldName, resultType: f.resultType,
    caption_EN: f.synonymEN || f.fieldName, caption_CN: null, width: 100, visible: true,
    sort: existing.reduce((m, x) => Math.max(m, x.sort), 0) + 1,
    exportSample: null, exportBlank: null, exportSpike: null, exportStandard: null, matchByHeader: false, importField: null,
  });

  const setBand = (name: BandName, range: string | null, sheet: number) => {
    setBands(b => [...b.filter(x => !(x.sheetIndex === sheet && x.name === name)), { sheetIndex: sheet, name, range }]);
  };
  const bandFromSelection = (name: BandName) => {
    const r = selection();
    if (!r) return;
    const range = `${a1(r.startRow, r.startCol)}:${a1(r.endRow, r.endCol)}`;
    setBand(name, range, r.sheetIndex);
    setBandSheet(r.sheetIndex);
    notify.info(`${BAND_LABEL[name]} band set to ${sheetName(r.sheetIndex)}!${range}.`);
  };

  const addReportHeader = async () => {
    const r = selection();
    if (!r) return;
    const caption = await prompt({ title: 'Report Header', message: `Report caption for the value in ${a1(r.startRow, r.startCol)}:`, required: true });
    if (!caption) return;
    setDraft(d => ({ ...d, headers: [...d.headers, { uqID: tempId(), sheetID: 0, caption: caption.trim(), position: a1(r.startRow, r.startCol) }] }));
  };
  const addReportDetail = async () => {
    const r = selection();
    if (!r) return;
    const name = await prompt({ title: 'Report Detail Column', message: `Report column name for sheet column ${colName(r.startCol)}:`, required: true });
    if (!name) return;
    setDraft(d => ({ ...d, details: [...d.details, { uqID: tempId(), sheetID: 0, columnName: name.trim(), columnIndex: r.startCol }] }));
  };

  /** Aligns each mapping's Formula with the formula its cell holds in the workbook — on live
   *  templates the workbook is routinely newer (edited in Excel). Only the mapping changes;
   *  the patcher then leaves those cells alone because they already hold that formula. */
  const takeWorkbookFormulas = () => {
    const cells = new Map(info.formulas.map(f => [`${f.sheetIndex}!${f.ref}`, f.formula]));
    const updates = new Map<number, string>();
    for (const p of draft.parsing) {
      const wbFormula = cells.get(`${sheetOf(p)}!${(p.position ?? '').toUpperCase()}`);
      if (wbFormula && (p.formula ?? '').trim() !== wbFormula) updates.set(p.uqID, wbFormula);
    }
    if (updates.size === 0) { notify.info('The mappings already match the workbook.'); return; }
    setDraft(d => ({ ...d, parsing: d.parsing.map(p => (updates.has(p.uqID) ? { ...p, formula: updates.get(p.uqID)! } : p)) }));
    notify.info(`${updates.size} mapping formula(s) taken from the workbook. Save to keep them.`);
  };

  const linkTest = ({ test, parameters }: LinkedTestPick) => {
    setDraft(d => ({
      ...d,
      tests: [...d.tests.filter(t => t.testMethodID.toUpperCase() !== test.id.toUpperCase()),
        ...parameters.map(p => ({
          uqID: 0, testMethodID: test.id, testParameterID: p.testParameterID, testName: test.testName,
          methodName: test.methodName, matrixName: test.matrixName, parameterName: p.parameterName,
          qcTypeName: p.qcTypeName, parameter: 'All', supportingParameter: null,
        }))],
    }));
    setLinkOpen(false);
  };

  // ── field list (drag onto the sheet) ─────────────────────────────────────
  // Legacy SDMS tab: the field panel has two lists, Raw Data and Calibration (as G5). The Toolbox
  // editor lists Raw Data only, as before.
  const [fieldTab, setFieldTab] = useState<'RawDataTable' | 'CalibrationTable'>('RawDataTable');
  const fieldList = useMemo(() => {
    const q = fieldSearch.trim().toLowerCase();
    const type = legacyTabs ? fieldTab : 'RawDataTable';
    return (lookups?.fields ?? []).filter(f => f.resultType === type && f.fieldName
      && (!q || `${f.fieldName} ${f.synonymEN ?? ''}`.toLowerCase().includes(q)));
  }, [lookups, fieldSearch, legacyTabs, fieldTab]);
  const mappedFieldIds = useMemo(() => new Set(draft.parsing.map(p => p.fieldID)), [draft.parsing]);
  /** The data-transfer editor's field list — legacy GenerateTransposedTable: the template's Field
   *  Info rows, upper-cased, as the columns of RawDataTableDataSource (unsaved rows included). */
  const dataSourceFields = useMemo(() => [...draft.fields].sort((a, b) => a.sort - b.sort)
    .filter(f => f.fieldName?.trim())
    .map(f => ({ name: f.fieldName!.trim().toUpperCase(), caption: f.caption_EN }))
    .filter((f, i, all) => all.findIndex(x => x.name === f.name) === i), [draft.fields]);

  // Legacy Data Transfer popup: the template's fields as two lists, RawData and CalibrationData.
  const sourceList = (cal: boolean) => [...draft.fields].sort((a, b) => a.sort - b.sort)
    .filter(f => f.fieldName?.trim() && (f.resultType === 'CalibrationTable') === cal)
    .map(f => ({ name: f.fieldName!.trim().toUpperCase(), caption: f.caption_EN }))
    .filter((f, i, all) => all.findIndex(x => x.name === f.name) === i);
  const rawSourceFields = useMemo(() => sourceList(false), [draft.fields]);   // eslint-disable-line react-hooks/exhaustive-deps
  const calSourceFields = useMemo(() => sourceList(true), [draft.fields]);    // eslint-disable-line react-hooks/exhaustive-deps

  const onFieldDropped = (at: { sheetIndex: number; row: number; col: number }, payload: string) => {
    const idNum = Number(payload);
    const field = (lookups?.fields ?? []).find(f => f.id === idNum);
    if (field) mapFieldAt(field, at);
  };
  const insertFieldAtSelection = (field: FieldOption) => {
    const r = selection();
    if (r) mapFieldAt(field, { sheetIndex: r.sheetIndex, row: r.startRow, col: r.startCol });
  };

  // ── conditional formats ──────────────────────────────────────────────────
  /** The sheet ribbon's Conditional Formatting menu: New Rule… (just the rule dialog) or
   *  Manage Rules… — both scoped to the sheet selection when there is one (Excel parity). */
  const onConditionalFormat = (cmd: ConditionalFormatCommand) => {
    if (cmd === 'new' && ro) return;
    const r = sheetRef.current?.getActiveRange();
    setCfManager({
      newRuleOnly: cmd === 'new',
      selection: r ? { sheetIndex: r.sheetIndex, startRow: r.startRow, startCol: r.startCol, endRow: r.endRow, endCol: r.endCol } : null,
    });
  };

  /** The Data tab's Data Validation menu — like Conditional Formatting, scoped to the selection. */
  const onDataValidation = (cmd: ConditionalFormatCommand) => {
    if (cmd === 'new' && ro) return;
    const r = sheetRef.current?.getActiveRange();
    setDvManager({
      newRuleOnly: cmd === 'new',
      selection: r ? { sheetIndex: r.sheetIndex, startRow: r.startRow, startCol: r.startCol, endRow: r.endRow, endCol: r.endCol } : null,
    });
  };

  // ── save / save as / delete ─────────────────────────────────────────────
  const save = async () => {
    if (!canWrite || loading) return;
    if (!draft.header.templateName.trim()) {
      setTab('general');
      await alert({ title: FORM_NAME, message: 'Template Name is required.', tone: 'error' });
      return;
    }
    const outputsProblem = outputsRef.current?.problem() ?? null;
    if (outputsProblem) {
      setTab('outputs');
      await alert({ title: FORM_NAME, message: outputsProblem, tone: 'error' });
      return;
    }
    // The sheet's edits + the conditional formats; typed/dropped fields update the mapping.
    const edits: SheetEdits = { ...sheetEdits(), conditionalFormats: cfEdits, dataValidations: dvEdits };
    const synced = syncMappingFromEdits(draft, edits, lookups?.fields ?? [], detailBandOf, tempId);
    const built = forSave(synced.template);
    // Legacy General Info edits the four stamps; tell the API to store them as entered.
    const body = legacyTabs ? { ...built, header: { ...built.header, stampsEdited: true } } : built;
    if (synced.template !== draft) setDraft(synced.template);

    let createdId: number | null = null;
    const workbookBase64 = upload ? toBase64(upload.buf) : null;
    await runSave({
      formName: FORM_NAME,
      auditAction: id ? 'edit' : 'create',
      save: async () => {
        if (id) {
          await sdmsTemplateApi.update(id, body, bands, workbookBase64, edits);
          await outputsRef.current?.save();   // the tab's new / edited rows, audited on the template
        }
        else createdId = await sdmsTemplateApi.create(body, bands, workbookBase64, edits);
      },
      onError: e => notify.error(errorText(e, 'Save failed.')),
      onSaved: () => {
        onSaved();
        if (createdId) onOpen(createdId); else void load(id);
      },
    });
  };

  const saveAs = async () => {
    if (!id || !canWrite) return;
    if (anyDirty()) {
      await alert({ title: 'Save As', message: 'Save or discard your changes first — Save As copies the saved template.', tone: 'warning' });
      return;
    }
    const name = await prompt({ title: 'Save As', message: 'Save this template as a new template named:',
      initialValue: `${saved.header.templateName} - Copy`, required: true, confirmLabel: 'Save As' });
    if (!name) return;
    try {
      const newId = await sdmsTemplateApi.copy(id, name.trim());
      notify.success(`Template "${name.trim()}" created.`);
      onSaved();
      onOpen(newId);
    } catch (e) {
      notify.error(errorText(e, 'Save As failed.'));
    }
  };

  const remove = async () => {
    if (!id) return;
    if (saved.header.inUse) {
      await alert({ title: FORM_NAME, message: 'This template cannot be deleted because data has already been entered with it. Retire it instead.', tone: 'warning' });
      return;
    }
    await runDelete({
      formName: FORM_NAME,
      message: `Delete the SDMS template "${saved.header.templateName}"? Its test links, data parsing and report columns are deleted with it.`,
      delete: async () => { await sdmsTemplateApi.remove(id); },
      onError: e => notify.error(errorText(e, 'Delete failed.')),
      onDeleted: () => { resetUnsavedTracking(); onSaved(); onClose(); },
    });
  };

  const onUpload = async (file: File | undefined) => {
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) { notify.error('Choose an Excel workbook (.xlsx).'); return; }
    if (storedBook && !blankStart && !(await confirm({ title: 'Replace Workbook',
      message: 'Replace the template workbook with this file? Cell mappings are kept; check Review after the upload.' }))) return;
    setUpload({ buf: await file.arrayBuffer(), name: file.name });
    setSheetReady(false);
  };

  const download = () => {
    if (!book) return;
    const url = URL.createObjectURL(new Blob([book], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(draft.header.templateName || 'SDMS-Template').replace(/[\\/:*?"<>|]+/g, '_')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── full-page layout (owner request 2026-10-01, Windows SpreadSheetTemplateBuilder parity) ──
  // The left navigation is hidden while a template is open (shared ai:nav-collapse / ai:nav-restore,
  // dispatched twice — see PlmResultEntryPage for why), the tab panel has a fixed, user-resizable
  // height, and the sheet fills the rest of the window.
  useEffect(() => {
    // Opened from Settings (legacy tabs) the navigation STAYS: that detail view is a normal settings
    // page, and the sheet has a tab of its own there (owner request 2026-10-07).
    if (legacyTabs) return;
    window.dispatchEvent(new Event('ai:nav-collapse'));
    const raf = requestAnimationFrame(() => window.dispatchEvent(new Event('ai:nav-collapse')));
    return () => { cancelAnimationFrame(raf); window.dispatchEvent(new Event('ai:nav-restore')); };
  }, [legacyTabs]);
  // Legacy tabs: the sheet shows on the SDMS tab and, as in G5, UNDER the grid on Parsing Data —
  // the tab where cells are mapped (owner request 2026-10-08).
  const sheetTab = tab === 'sheet' || tab === 'mapping';
  // Under Parsing Data the sheet has NO field panel (G5 shows a bare sheet there), so the sheet
  // changes width between the two tabs — tell its canvas to measure again.
  useEffect(() => {
    if (!legacyTabs || !sheetTab) return;
    const raf = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    return () => cancelAnimationFrame(raf);
  }, [legacyTabs, sheetTab, tab]);
  const [panelOpen, setPanelOpen] = useState(() => readPref(PANEL_OPEN_KEY) !== '0');
  const [panelHeight, setPanelHeight] = useState(() => clampPanel(Number(readPref(PANEL_H_KEY)) || PANEL_DEFAULT_H));
  useEffect(() => { writePref(PANEL_OPEN_KEY, panelOpen ? '1' : '0'); }, [panelOpen]);
  useEffect(() => { writePref(PANEL_H_KEY, String(panelHeight)); }, [panelHeight]);
  const startPanelResize = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startY = e.clientY, startH = panelHeight;
    const move = (ev: PointerEvent) => setPanelHeight(clampPanel(startH + ev.clientY - startY));
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  // Field list = an AUTO-HIDE DOCK PANEL on the left, exactly like the Windows builder (owner request
  // 2026-10-01): PINNED it is a docked column; UNPINNED it shrinks to a vertical "Field List" tab on the
  // left edge and slides out over the sheet while the pointer is on it (or after a click), sliding back
  // when the pointer leaves. It is never hidden outright. Only pinned/unpinned is remembered.
  const [fieldListPinned, setFieldListPinned] = useState(() => readPref(FIELDLIST_DOCK_KEY) !== '0');
  useEffect(() => { writePref(FIELDLIST_DOCK_KEY, fieldListPinned ? '1' : '0'); }, [fieldListPinned]);
  const [fieldListPeek, setFieldListPeek] = useState(false);
  // A field dragged out of the slid-out panel must not lose its source mid-drag, so the panel stays out
  // until the drag ends; a short grace period lets the pointer cross the gap between tab and panel.
  const fieldDragRef = useRef(false);
  const peekTimer = useRef<number | null>(null);
  const showPeek = () => { if (peekTimer.current) window.clearTimeout(peekTimer.current); setFieldListPeek(true); };
  const hidePeekSoon = () => {
    if (peekTimer.current) window.clearTimeout(peekTimer.current);
    peekTimer.current = window.setTimeout(() => { if (!fieldDragRef.current) setFieldListPeek(false); }, 350);
  };
  useEffect(() => () => { if (peekTimer.current) window.clearTimeout(peekTimer.current); }, []);
  // The sheet canvas measures its host; tell it the docked column appeared / went away.
  useEffect(() => {
    if (!fieldListPinned) setFieldListPeek(false);
    const raf = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    return () => cancelAnimationFrame(raf);
  }, [fieldListPinned]);

  // The sheet row takes the window height left below it (it sits in the scrolling page, so a CSS
  // flex fill cannot reach it). Re-measured on resize, panel changes and once layout settles.
  const sheetRowRef = useRef<HTMLDivElement | null>(null);
  const [sheetRowHeight, setSheetRowHeight] = useState<number | null>(null);
  useEffect(() => {
    const fit = () => {
      const top = sheetRowRef.current?.getBoundingClientRect().top;
      if (top == null) return;
      setSheetRowHeight(Math.max(SHEET_MIN_H, Math.floor(window.innerHeight - top - 16)));
    };
    fit();
    const raf = requestAnimationFrame(fit);
    window.addEventListener('resize', fit);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', fit); };
  }, [panelOpen, panelHeight, tab, upload, canWrite, loading]);

  /** "Update" on a stored template, "Save" on a new one (owner request 2026-10-01). */
  const saveLabel = id ? 'Update' : 'Save';
  /** The sheet ribbon's "Template" tab (Windows builder keeps these in its spreadsheet ribbon). The
   *  designer looks each `run` up on click, so these closures always see the current draft. */
  const templateActions: TemplateRibbonAction[] = [
    { id: 'stb-tpl-map-field', title: 'Map Field', icon: 'ti-link',
      tooltip: 'Map a field to the selected cell', run: () => { if (selection()) setPicker('map'); } },
    { id: 'stb-tpl-header-band', title: 'Set Header Band', icon: 'ti-layout-navbar',
      tooltip: 'Make the selected range the Header band', run: () => bandFromSelection('HEADERRANGE') },
    { id: 'stb-tpl-detail-band', title: 'Set Detail Band', icon: 'ti-layout-rows',
      tooltip: 'Make the selected range the Detail band (repeats per sample)', run: () => bandFromSelection('DETAILRANGE') },
  ];
  /** New: a blank template on an empty sheet (from an open template, via the page's New route). */
  const startNew = () => leave(() => (id ? onOpen(0) : void load(null)));
  useHotkeys([
    { combo: 'mod+s', label: saveLabel, group: FORM_NAME, run: () => void save(), disabled: !canWrite || loading },
    // New moved out of AppToolbar's own slot (to sit in the right-hand Save set), so it registers its key here.
    { combo: 'alt+n', label: 'New', group: FORM_NAME, run: startNew, disabled: !canWrite || loading },
    { combo: 'alt+e', label: 'Edit', group: FORM_NAME, run: () => setMode('edit'), disabled: !viewing || !perm.canWrite || loading },
    { combo: 'f2', label: 'Edit', group: FORM_NAME, run: () => setMode('edit'), disabled: !viewing || !perm.canWrite || loading },
  ]);

  // ── grids ───────────────────────────────────────────────────────────────
  const groupedTests = useMemo<GroupedTest[]>(() => {
    const m = new Map<string, GroupedTest & { list: string[] }>();
    for (const t of draft.tests) {
      const key = t.testMethodID.toUpperCase();
      const g = m.get(key) ?? { testMethodID: key, testName: t.testName ?? key, methodName: t.methodName ?? '', matrixName: t.matrixName ?? '', parameters: '', list: [] };
      g.list.push([t.parameterName, t.qcTypeName].filter(Boolean).join(' / '));
      m.set(key, g);
    }
    return [...m.values()].map(g => ({ ...g, parameters: `${g.list.length}: ${g.list.join(', ')}` }));
  }, [draft.tests]);

  const testCols: AppColumn<GroupedTest>[] = [
    { field: 'testName', header: 'Test', width: 200 },
    { field: 'methodName', header: 'Method', width: 150 },
    { field: 'matrixName', header: 'Matrix', width: 120 },
    { field: 'parameters', header: 'Parameters', width: 420, wrap: true },
  ];

  const editable = canWrite;
  /** A Yes/No mapping flag shown as a checkbox (legacy parity); ticking it edits the draft directly. */
  const mapCheck = (key: 'continuous' | 'read' | 'write', header: string, width: number): AppColumn<TemplateParsing> => ({
    field: key, header, width, align: 'center', text: r => yn(r[key]), value: r => yn(r[key]),
    render: r => (
      <input type="checkbox" checked={r[key]} disabled={!editable} aria-label={`${header} — ${r.position}`}
        onClick={e => e.stopPropagation()}
        onChange={e => { const v = e.target.checked; setDraft(d => ({ ...d, parsing: d.parsing.map(p => (p.uqID === r.uqID ? { ...p, [key]: v } : p)) })); }} />
    ),
  });
  const mapCols: AppColumn<TemplateParsing>[] = [
    { field: 'sheetID', header: 'Sheet', width: 110, text: r => sheetName(sheetOf(r)), value: r => sheetName(sheetOf(r)) },
    { field: 'position', header: 'Cell', width: 70 },
    { field: 'fieldName', header: 'Field', width: 170 },
    { field: 'caption', header: 'Caption', width: 160, edit: editable ? { type: 'text' } : undefined },
    { field: 'formula', header: 'Formula', width: 260, edit: editable ? { type: 'text' } : undefined },
    { field: 'format', header: 'Format', width: 90, edit: editable ? { type: 'text' } : undefined },
    mapCheck('continuous', 'Continuous', 95),
    mapCheck('read', 'Read', 70),
    mapCheck('write', 'Write', 70),
    { field: 'defaultValue', header: 'Default Value', width: 120, edit: editable ? { type: 'text' } : undefined },
  ];
  const commitMap = (row: TemplateParsing, col: AppColumn<TemplateParsing>, v: string) => {
    const bool = v === 'Yes';
    setDraft(d => ({
      ...d,
      parsing: d.parsing.map(p => p.uqID !== row.uqID ? p : {
        ...p,
        ...(col.field === 'continuous' ? { continuous: bool } : col.field === 'read' ? { read: bool } : col.field === 'write' ? { write: bool }
          : { [col.field]: v.trim() === '' ? null : v }),
      }),
    }));
  };

  // ── legacy Parsing Data (G5 grid: Add · Remove, every column typed in the row) ──────────
  /** uqID of the Parsing Data row whose FieldName popup is open. */
  const [fieldNameRow, setFieldNameRow] = useState<number | null>(null);
  const pickFieldName = (f: FieldOption) => {
    setDraft(d => ({
      ...d,
      parsing: d.parsing.map(p => p.uqID !== fieldNameRow ? p
        // Caption is left as the user has it (owner request 2026-10-08) — picking a field does not fill it.
        : { ...p, fieldID: f.id, fieldName: f.fieldName, resultType: f.resultType }),
    }));
    setFieldNameRow(null);
  };
  const runTypeOf = (r: TemplateParsing) => (r.resultType === 'CalibrationTable' ? 'Calibration table' : 'Raw data table');
  const addParsingRow = () => setDraft(d => ({
    ...d,
    parsing: [...d.parsing, {
      uqID: tempId(), sheetID: d.header.dtSheetID < 1 ? 1 : d.header.dtSheetID, fieldID: 0, fieldName: null,
      resultType: 'RawDataTable', position: null, formula: null, format: null, continuous: false, read: false,
      write: false, caption: null, defaultValue: null, usersFromDepartmentChain: false,
    }],
  }));
  const legacyMapCols: AppColumn<TemplateParsing>[] = [
    { field: 'sheetID', header: 'SheetID', type: 'number', width: 80, edit: editable ? { type: 'number' } : undefined },
    { field: 'resultType', header: 'RunType', width: 130, text: runTypeOf, value: runTypeOf },
    // FieldName is chosen in the legacy FieldName popup, opened by clicking the cell (G5).
    { field: 'fieldName', header: 'FieldName', width: 190,
      render: r => (
        <span className={`gedit-cell stb-fieldname-cell${r.fieldName ? '' : ' req-empty-cell'}${editable ? '' : ' disabled'}`}
          role="button" tabIndex={editable ? 0 : -1} title={editable ? 'Click to choose the field' : undefined}
          onClick={e => { e.stopPropagation(); if (editable) setFieldNameRow(r.uqID); }}
          onKeyDown={e => { if (editable && (e.key === 'Enter' || e.key === 'F4')) { e.preventDefault(); e.stopPropagation(); setFieldNameRow(r.uqID); } }}>
          {r.fieldName ?? ''}
        </span>
      ) },
    { field: 'caption', header: 'Caption', width: 150, edit: editable ? { type: 'text' } : undefined },
    { field: 'position', header: 'Position', width: 90,
      edit: editable ? { type: 'text', requiredEmpty: r => !(r.position ?? '').trim() } : undefined },
    { field: 'formula', header: 'Formula', width: 220, edit: editable ? { type: 'text' } : undefined },
    { field: 'format', header: 'Format', width: 90, edit: editable ? { type: 'text' } : undefined },
    mapCheck('continuous', 'Continuous', 95),
    mapCheck('read', 'Read', 70),
    mapCheck('write', 'Write', 70),
    { field: 'defaultValue', header: 'DefaultValue', width: 120, edit: editable ? { type: 'text' } : undefined },
    { field: 'usersFromDepartmentChain', header: 'UsersFromDepartmentChain', width: 200, align: 'center',
      text: r => yn(r.usersFromDepartmentChain), value: r => yn(r.usersFromDepartmentChain),
      render: r => (
        <input type="checkbox" checked={r.usersFromDepartmentChain} disabled={!editable}
          aria-label={`UsersFromDepartmentChain — ${r.position ?? ''}`} onClick={e => e.stopPropagation()}
          onChange={e => { const v = e.target.checked; setDraft(d => ({ ...d, parsing: d.parsing.map(p => (p.uqID === r.uqID ? { ...p, usersFromDepartmentChain: v } : p)) })); }} />
      ) },
  ];
  const commitLegacyMap = (row: TemplateParsing, col: AppColumn<TemplateParsing>, v: string) => {
    const text = v.trim();
    setDraft(d => ({
      ...d,
      parsing: d.parsing.map(p => {
        if (p.uqID !== row.uqID) return p;
        if (col.field === 'sheetID') return { ...p, sheetID: Math.max(1, Number.parseInt(text, 10) || 1) };
        if (col.field === 'position') return { ...p, position: text ? text.replace(/\$/g, '').toUpperCase() : null };
        return { ...p, [col.field]: text === '' ? null : v };
      }),
    }));
  };

  const fieldCols: AppColumn<TemplateField>[] = [
    { field: 'fieldName', header: 'Field', width: 170 },
    { field: 'caption_EN', header: 'Caption', width: 170, edit: editable ? { type: 'text' } : undefined },
    { field: 'width', header: 'Width', type: 'number', width: 70, edit: editable ? { type: 'number' } : undefined },
    { field: 'sort', header: 'Sort', type: 'number', width: 60, edit: editable ? { type: 'number' } : undefined },
    { field: 'visible', header: 'Visible', width: 70, text: r => yn(r.visible), value: r => yn(r.visible), edit: editable ? { type: 'combo', options: YES_NO } : undefined },
    { field: 'exportSample', header: 'Export Sample', width: 120, edit: editable ? { type: 'text' } : undefined },
    { field: 'exportBlank', header: 'Export Blank', width: 110, edit: editable ? { type: 'text' } : undefined },
    { field: 'exportSpike', header: 'Export Spike', width: 110, edit: editable ? { type: 'text' } : undefined },
    { field: 'exportStandard', header: 'Export Standard', width: 120, edit: editable ? { type: 'text' } : undefined },
    { field: 'matchByHeader', header: 'Match By Header', width: 120, text: r => yn(r.matchByHeader), value: r => yn(r.matchByHeader), edit: editable ? { type: 'combo', options: YES_NO } : undefined },
    { field: 'importField', header: 'Import Field', width: 120, edit: editable ? { type: 'text' } : undefined },
  ];
  // ── legacy Field Info (Template | Calibration Field) ────────────────────────
  const [fiTab, setFiTab] = useState<'RawDataTable' | 'CalibrationTable'>('RawDataTable');
  const fiAvailable = useMemo(() => {
    const used = new Set(draft.fields.map(f => f.fieldID));
    return (lookups?.fields ?? []).filter(f => f.resultType === fiTab && f.fieldName && !used.has(f.id));
  }, [lookups, draft.fields, fiTab]);
  // A row whose result type is unknown (an old row) is listed under Template rather than hidden.
  const fiChosen = useMemo(() => draft.fields.filter(f =>
    fiTab === 'CalibrationTable' ? f.resultType === 'CalibrationTable' : f.resultType !== 'CalibrationTable'),
  [draft.fields, fiTab]);
  const removeFieldRow = (f: TemplateField) => setDraft(d => ({ ...d, fields: d.fields.filter(x => x.fieldID !== f.fieldID) }));
  const addFieldRow = (f: FieldOption) => setDraft(d => (d.fields.some(x => x.fieldID === f.id) ? d
    : { ...d, fields: [...d.fields, { ...newFieldRow(f, d.fields), caption_CN: null }] }));
  const legacyFieldCols: AppColumn<TemplateField>[] = [
    { field: 'fieldName', header: 'FieldName', width: 170 },
    { field: 'caption_EN', header: 'Caption_EN', width: 170, edit: editable ? { type: 'text' } : undefined },
    { field: 'caption_CN', header: 'Caption_CN', width: 150, edit: editable ? { type: 'text' } : undefined },
    { field: 'width', header: 'Width', type: 'number', width: 80, edit: editable ? { type: 'number' } : undefined },
    { field: 'sort', header: 'Sort', type: 'number', width: 70, edit: editable ? { type: 'number' } : undefined },
    { field: 'visible', header: 'Visible', width: 80, text: r => yn(r.visible), value: r => yn(r.visible), edit: editable ? { type: 'combo', options: YES_NO } : undefined },
    // ExportSample is not listed here (owner request 2026-10-07); its stored value is left as it is.
    { field: 'importField', header: 'ImportField', width: 130, edit: editable ? { type: 'text' } : undefined },
  ];
  const commitField = (row: TemplateField, col: AppColumn<TemplateField>, v: string) => {
    setDraft(d => ({
      ...d,
      fields: d.fields.map(f => f.fieldID !== row.fieldID ? f : {
        ...f,
        ...(col.field === 'visible' ? { visible: v === 'Yes' } : col.field === 'matchByHeader' ? { matchByHeader: v === 'Yes' }
          : col.field === 'width' || col.field === 'sort' ? { [col.field]: Number.parseInt(v, 10) || 0 }
          : { [col.field]: v.trim() === '' ? null : v }),
      }),
    }));
  };

  const headerCols: AppColumn<TemplateHeaderMap>[] = [
    { field: 'caption', header: 'Report Caption', width: 240, edit: editable ? { type: 'text' } : undefined },
    { field: 'position', header: 'Cell', width: 90, edit: editable ? { type: 'text', pattern: '^\\$?[A-Za-z]{1,3}\\$?[1-9][0-9]*$', patternMessage: 'Enter a cell such as D4.' } : undefined },
  ];
  const detailCols: AppColumn<TemplateDetailMap>[] = [
    { field: 'columnName', header: 'Report Column', width: 240, edit: editable ? { type: 'text' } : undefined },
    { field: 'columnIndex', header: 'Sheet Column', width: 110, text: r => colName(r.columnIndex), value: r => colName(r.columnIndex),
      edit: editable ? { type: 'text', pattern: '^[A-Za-z]{1,3}$', patternMessage: 'Enter a column letter such as D.' } : undefined },
  ];

  // Legacy Data Source Mapping: the same maps under the G5 captions.
  const [dsTab, setDsTab] = useState<(typeof DS_TABS)[number]>('Header');
  const legacyHeaderCols: AppColumn<TemplateHeaderMap>[] = [
    { ...headerCols[0]!, header: 'CaptionMapping', width: 520 },
    { ...headerCols[1]!, header: 'PositionMapping', width: 320 },
  ];
  const legacyDetailCols: AppColumn<TemplateDetailMap>[] = [
    // G5 captions the Detail grid differently from Header: ColumnMapping · ColumnIndex.
    { ...detailCols[0]!, header: 'ColumnMapping', width: 520 },
    { ...detailCols[1]!, header: 'ColumnIndex', width: 320 },
  ];

  const review = useMemo(() => reviewTemplate(draft, info, bands, !!book), [draft, info, bands, book]);
  const errors = review.filter(r => r.level === 'error').length;

  const traceSet = new Set((draft.header.traceability ?? '').split(',').map(s => s.trim()).filter(Boolean));
  const toggleTrace = (name: string, on: boolean) => {
    const next = TRACEABILITY.filter(t => (t === name ? on : traceSet.has(t)));
    setHeader({ traceability: next.length ? next.join(', ') : null });
  };

  const h = draft.header;
  const title = id ? (saved.header.templateName || `Template ${id}`) : 'New Template';
  const ro = !canWrite;

  return (
    <div className="bl-page stb-page">
      <div className="bl-top">
        <div className="dp-breadcrumb">
          <span className="dp-bc-link" onClick={() => leave(() => onHome?.())}>Home</span>
          <i className="ti ti-chevron-right" />
          {legacyTabs ? <><span>Settings</span><i className="ti ti-chevron-right" /><span>SDMS</span></> : <span>Toolbox</span>}
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-link" onClick={() => leave(onClose)}>SDMS Template Builder</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{title}{id ? ` (#${id})` : ''}</span>
        </div>
        <FormTitle pageId={PAGE_ID} auditFormName={FORM_NAME} auditSourceId={id ? String(id) : undefined} loadMs={loadMs} loading={loading} />
      </div>

      <div className="bl-grid-area stb-area">
        <AppToolbar
          left={legacyTabs ? undefined : <>
            <button type="button" className="ca-btn" onClick={() => fileRef.current?.click()} disabled={!canWrite || loading}>
              <i className="ti ti-upload" /> Upload Workbook</button>
            <button type="button" className="ca-btn" onClick={download} disabled={!book}>
              <i className="ti ti-download" /> Download Workbook</button>
          </>}
          // The record actions sit flush RIGHT, like every detail form's Save set (SettingsDetailPage);
          // the Loaded / Audit chips go after the left-hand actions so nothing trails Close.
          showMeta={false}
          right={<><FormMetaChips /><span className="stb-save-set">
            {viewing && perm.canWrite && <button type="button" className="ca-btn ca-btn-solid" onClick={() => setMode('edit')} disabled={loading}
              title={`Edit (${formatCombo('alt+e')})`}><i className="ti ti-pencil" /> Edit</button>}
            {!viewing && <><button type="button" className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={!canWrite || loading}
              title={`${saveLabel} (${formatCombo('mod+s')})`}><i className="ti ti-device-floppy" /> {saveLabel}</button>
            <button type="button" className="ca-btn" onClick={() => void saveAs()} disabled={!id || !canWrite || loading}>
              <i className="ti ti-copy" /> Save As</button>
            {canWrite && <button type="button" className="ca-btn" onClick={startNew} disabled={loading}
              title={`New (${formatCombo('alt+n')})`}><i className="ti ti-plus" /> New</button>}</>}
            <button type="button" className="ca-btn" onClick={() => leave(onClose)}>
              <i className="ti ti-arrow-back-up" /> Close</button>
          </span></>}
          onDelete={id && perm.canDelete && !viewing ? () => void remove() : undefined}
          onRefresh={() => leave(() => void load(id))}
          refreshing={loading}
        />
        <input ref={fileRef} type="file" accept=".xlsx" hidden data-unsaved-ignore
          onChange={e => { void onUpload(e.target.files?.[0]); e.target.value = ''; }} />

        {upload && <div className="stb-banner"><i className="ti ti-info-circle" /> New workbook <b>{upload.name}</b> — saved with the template on Save.</div>}
        {!perm.canWrite && <div className="stb-banner"><i className="ti ti-lock" /> Read only — you do not have Write permission for SDMS.</div>}

        <div className={`qcb-tabs stb-tabs${legacyTabs ? ' stb-tabs--boxed' : ''}`} role="tablist">
          {(legacyTabs ? LEGACY_TABS : TABS).map(t => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
              className={`qcb-tab stb-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
              {t.label}
              {t.id === 'tests' && <span className="stb-count">{groupedTests.length}</span>}
              {t.id === 'mapping' && <span className="stb-count">{draft.parsing.length}</span>}
              {t.id === 'fields' && <span className="stb-count">{draft.fields.length}</span>}
              {t.id === 'review' && review.length > 0 && <span className={`stb-count${errors ? ' err' : ''}`}>{review.length}</span>}
            </button>
          ))}
          {/* Map Field / Set Header Band / Set Detail Band live on the sheet ribbon's own "Template" tab
              (owner request 2026-10-01); only the panel fold toggle stays here, at the end of the tab row. */}
          <span className="stb-tabs-actions" hidden={legacyTabs}>
            <button type="button" className="ca-btn stb-panel-toggle" onClick={() => setPanelOpen(o => !o)}
              title={panelOpen ? 'Hide the tab panel — give the sheet the whole page' : 'Show the tab panel'}
              aria-expanded={panelOpen} aria-label={panelOpen ? 'Hide tab panel' : 'Show tab panel'}>
              <i className={`ti ${panelOpen ? 'ti-chevron-up' : 'ti-chevron-down'}`} />
            </button>
          </span>
        </div>

        {/* Legacy SDMS tab: no panel — the sheet below takes the whole page. */}
        <section className={`stb-panel${legacyTabs ? ` stb-panel--page stb-panel--boxed${tab === 'mapping' ? ' stb-panel--over-sheet' : ''}` : ''}`}
          hidden={legacyTabs ? tab === 'sheet' : !panelOpen}
          style={legacyTabs ? undefined : { height: panelHeight }}>
          {/* Legacy General Info (owner request 2026-10-07): the G5 detail layout — one column of fields on
              the left, in G5 order and captions, Data Model as radio buttons on the right. Same header
              values as the Toolbox layout below; nothing here is stored differently. */}
          {tab === 'general' && legacyTabs && (
            <div className="stb-g5">
              <div className="sd-grid sd-grid--side sd-grid--square stb-g5-col">
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-name">Template Name<span className="req">*</span></label>
                  <input id="stb-name" className={`sd-input${ro ? ' readonly' : ''}`}
                    value={h.templateName} maxLength={200} readOnly={ro} onChange={e => setHeader({ templateName: e.target.value })} />
                </div>
                <div className="sd-row">
                  {/* The G5 Test field names the tests the template serves; here those are the LINKED
                      tests (what SDMS Result Entry reads), picked with Link Test. */}
                  <label className="sd-label" htmlFor="stb-test">Test<span className="req">*</span></label>
                  {/* Clicking the field opens the legacy Test popup (Available Test · Selected Test ·
                      Selected Parameter), which adds AND removes tests. */}
                  <input id="stb-test" className={`sd-input stb-g5-test${ro ? ' readonly' : ''}`} readOnly
                    value={[...new Set(groupedTests.map(t => t.testName))].join(', ')}
                    title="Click to choose the tests"
                    onClick={() => { if (!ro) setTestsOpen(true); }}
                    onKeyDown={e => { if (!ro && (e.key === 'Enter' || e.key === ' ' || e.key === 'F4')) { e.preventDefault(); setTestsOpen(true); } }} />
                </div>
                <div className="sd-row">
                  <span className="sd-label">Template Type</span>
                  {/* The shared combo, not a native <select>: its list is capped and scrolls, where the
                      native one opened every type at once (owner request 2026-10-07). */}
                  <ComboSelect
                    className="sd-combo"
                    value={h.templateTypeId == null ? '' : String(h.templateTypeId)}
                    options={(lookups?.templateTypes ?? []).map(t => ({ value: String(t.id), label: t.name ?? '' }))}
                    placeholder="N/A"
                    disabled={ro}
                    clearable
                    onChange={v => setHeader({ templateTypeId: v ? Number(v) : null })}
                  />
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-cal">Calibration Level No</label>
                  <input id="stb-cal" type="number" min={0} className={`sd-input${ro ? ' readonly' : ''}`} readOnly={ro}
                    value={h.calibrationLevelNo} onChange={e => setHeader({ calibrationLevelNo: Math.max(0, Number.parseInt(e.target.value, 10) || 0) })} />
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-source">Source</label>
                  <input id="stb-source" className={`sd-input${ro ? ' readonly' : ''}`} readOnly={ro} maxLength={200}
                    value={h.source ?? ''} onChange={e => setHeader({ source: e.target.value || null })} />
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-sheet">Data Transfer SheetID</label>
                  <input id="stb-sheet" type="number" min={1} className={`sd-input${ro ? ' readonly' : ''}`} readOnly={ro}
                    value={h.dtSheetID < 1 ? 1 : h.dtSheetID}
                    onChange={e => setHeader({ dtSheetID: Math.max(1, Number.parseInt(e.target.value, 10) || 1) })} />
                </div>
                {/* Editable, as G5 (owner decision 2026-10-07): what is entered here is what is stored —
                    the save does not overwrite these four with the signed-in user and the time. */}
                <div className="sd-row">
                  <span className="sd-label">Entered By</span>
                  <ComboSelect
                    className="sd-combo"
                    value={(h.enteredById ?? '').toLowerCase()}
                    options={employees}
                    placeholder="N/A"
                    disabled={ro}
                    clearable
                    onChange={v => setHeader({ enteredById: v || null })}
                  />
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-edt">Entered Date</label>
                  <input id="stb-edt" type="datetime-local" className={`sd-input${ro ? ' readonly' : ''}`} readOnly={ro}
                    value={(h.enteredDate ?? '').slice(0, 16)}
                    onChange={e => setHeader({ enteredDate: e.target.value || null })} />
                </div>
                <div className="sd-row">
                  <span className="sd-label">Modified By</span>
                  <ComboSelect
                    className="sd-combo"
                    value={(h.modifiedById ?? '').toLowerCase()}
                    options={employees}
                    placeholder="N/A"
                    disabled={ro}
                    clearable
                    onChange={v => setHeader({ modifiedById: v || null })}
                  />
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-mdt">Modified Date</label>
                  <input id="stb-mdt" type="datetime-local" className={`sd-input${ro ? ' readonly' : ''}`} readOnly={ro}
                    value={(h.modifiedDate ?? '').slice(0, 16)}
                    onChange={e => setHeader({ modifiedDate: e.target.value || null })} />
                </div>
                <div className="sd-row stb-g5-top">
                  <span className="sd-label">Orientation</span>
                  <div className="stb-g5-stack">
                    <label><input type="radio" name="stb-orient" disabled={ro} checked={!h.orientation} onChange={() => setHeader({ orientation: false })} /> Landscape</label>
                    <label><input type="radio" name="stb-orient" disabled={ro} checked={h.orientation} onChange={() => setHeader({ orientation: true })} /> Vertical</label>
                    <label><input type="checkbox" disabled={ro} checked={h.isRetire} onChange={e => setHeader({ isRetire: e.target.checked, isActive: e.target.checked ? false : h.isActive })} /> Retire</label>
                    <label><input type="checkbox" disabled={ro} checked={h.isActive} onChange={e => setHeader({ isActive: e.target.checked, isRetire: e.target.checked ? false : h.isRetire })} /> Active</label>
                  </div>
                </div>
              </div>
              <div className="sd-grid sd-grid--side sd-grid--square stb-g5-col">
                <div className="sd-row stb-g5-top">
                  <span className="sd-label">Data Model</span>
                  <div className="stb-g5-stack" role="radiogroup" aria-label="Data Model">
                    {LEGACY_MODELS.map(m => (
                      <label key={m.id}><input type="radio" name="stb-model" disabled={ro} checked={h.model === m.id}
                        onChange={() => setHeader({ model: m.id })} /> {m.label}</label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Toolbox General Info (owner layout 2026-10-12): four columns — 1 Name · Data Model ·
              Orientation, 2 left free, 3 Template Type · Data Sheet · Calibration Levels, 4 SDMS Version ·
              Template Version (read-only, stamped by the save) · Template Source; then Traceability, Status
              and the Created / Modified line on one row. */}
          {tab === 'general' && !legacyTabs && (
            <div className="stb-general stb-general4">
              <div className="sd-grid sd-grid--side stb-gcol stb-gcol--1">
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-name">Template Name<span className="req">*</span></label>
                  <input id="stb-name" className={`sd-input${h.templateName.trim() ? '' : ' required-empty'}${ro ? ' readonly' : ''}`}
                    value={h.templateName} maxLength={200} readOnly={ro} onChange={e => setHeader({ templateName: e.target.value })} />
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-model">Data Model</label>
                  <select id="stb-model" className={`sd-input${ro ? ' readonly' : ''}`} disabled={ro} value={h.model}
                    onChange={e => setHeader({ model: Number(e.target.value) })}>
                    {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-orient">Orientation</label>
                  <select id="stb-orient" className={`sd-input${ro ? ' readonly' : ''}`} disabled={ro} value={h.orientation ? 'v' : 'l'}
                    onChange={e => setHeader({ orientation: e.target.value === 'v' })}>
                    <option value="l">Landscape</option>
                    <option value="v">Vertical</option>
                  </select>
                </div>
              </div>
              <div className="stb-gcol stb-gcol--spare" aria-hidden />
              <div className="sd-grid sd-grid--side stb-gcol stb-gcol--3">
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-type">Template Type</label>
                  <select id="stb-type" className={`sd-input${ro ? ' readonly' : ''}`} disabled={ro} value={h.templateTypeId ?? ''}
                    onChange={e => setHeader({ templateTypeId: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">(none)</option>
                    {(lookups?.templateTypes ?? []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-sheet">Data Sheet</label>
                  <select id="stb-sheet" className={`sd-input${ro ? ' readonly' : ''}`} disabled={ro} value={h.dtSheetID < 1 ? 1 : h.dtSheetID}
                    onChange={e => setHeader({ dtSheetID: Number(e.target.value) })}>
                    {(info.sheets.length ? info.sheets : ['Sheet1']).map((s, i) => <option key={s} value={i + 1}>{i + 1} — {s}</option>)}
                  </select>
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-cal">Calibration Levels</label>
                  <input id="stb-cal" type="number" min={0} className={`sd-input${ro ? ' readonly' : ''}`} readOnly={ro}
                    value={h.calibrationLevelNo} onChange={e => setHeader({ calibrationLevelNo: Math.max(0, Number.parseInt(e.target.value, 10) || 0) })} />
                </div>
              </div>
              <div className="sd-grid sd-grid--side stb-gcol stb-gcol--4">
                {/* Read-only: set by the server on every save, never typed. */}
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-sdmsver">SDMS Version</label>
                  <input id="stb-sdmsver" className="sd-input readonly" readOnly tabIndex={-1}
                    value={saved.header.sdmsVersion ?? ''} />
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-tplver">Template Version</label>
                  <input id="stb-tplver" className="sd-input readonly" readOnly tabIndex={-1}
                    value={saved.header.templateVersion ?? ''} />
                </div>
                <div className="sd-row">
                  <label className="sd-label" htmlFor="stb-source">Template Source</label>
                  <input id="stb-source" className={`sd-input${ro ? ' readonly' : ''}`} readOnly={ro} maxLength={200}
                    value={h.source ?? ''} onChange={e => setHeader({ source: e.target.value || null })} />
                </div>
              </div>
              <div className="sd-grid sd-grid--side stb-gcol stb-gcol--trace">
                <div className="sd-row">
                  <span className="sd-label">Traceability</span>
                  <div className="stb-checks">
                    {TRACEABILITY.map(t => (
                      <label key={t}><input type="checkbox" disabled={ro} checked={traceSet.has(t)} onChange={e => toggleTrace(t, e.target.checked)} /> {t}</label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="sd-grid sd-grid--side stb-gcol stb-gcol--3">
                <div className="sd-row">
                  <span className="sd-label">Status</span>
                  <div className="stb-checks stb-checks--pairs">
                    <label><input type="checkbox" disabled={ro} checked={h.isActive} onChange={e => setHeader({ isActive: e.target.checked, isRetire: e.target.checked ? false : h.isRetire })} /> Active</label>
                    <label><input type="checkbox" disabled={ro} checked={h.isRetire} onChange={e => setHeader({ isRetire: e.target.checked, isActive: e.target.checked ? false : h.isActive })} /> Retired</label>
                    <label><input type="checkbox" disabled={ro} checked={h.isSampleBased} onChange={e => setHeader({ isSampleBased: e.target.checked })} /> Sample Based</label>
                    <label><input type="checkbox" disabled={ro} checked={h.isSampling} onChange={e => setHeader({ isSampling: e.target.checked })} /> Sampling</label>
                  </div>
                </div>
              </div>
              <div className="stb-gcol stb-gcol--4 stb-meta">
                {id ? (
                  <>
                    <div>Created {fmtLocalDateTime(saved.header.enteredDate)} by {saved.header.enteredByName ?? '—'}</div>
                    <div>Modified {fmtLocalDateTime(saved.header.modifiedDate)} by {saved.header.modifiedByName ?? '—'}</div>
                  </>
                ) : null}
              </div>
            </div>
          )}

          {tab === 'tests' && (
            <>
              <div className="stb-actions">
                <button type="button" className="ca-btn" disabled={ro} onClick={() => setLinkOpen(true)}><i className="ti ti-link" /> Link Test</button>
                <button type="button" className="ca-btn" disabled={ro || selTests.size === 0}
                  onClick={() => { setDraft(d => ({ ...d, tests: d.tests.filter(t => !selTests.has(t.testMethodID.toUpperCase())) })); setSelTests(new Set()); }}>
                  <i className="ti ti-unlink" /> Unlink</button>
                <span className="stb-hint">SDMS Result Entry offers this template for the tests linked here.</span>
              </div>
              <AppDataGrid<GroupedTest> rows={groupedTests} columns={testCols} getRowId={r => r.testMethodID}
                selectable={!ro} selectedIds={selTests} onSelectionChange={setSelTests} defaultPageSize={10}
                hideHeaderTools emptyText="No test linked yet." />
            </>
          )}

          {/* Legacy Parsing Data (G5): Add puts a blank row in the grid and every column is typed in the
              row; Remove takes the ticked rows out. The server still requires a field and a valid cell
              on every row at Save. */}
          {tab === 'mapping' && legacyTabs && (
            <>
              <div className="stb-actions">
                <button type="button" className="ca-btn" disabled={ro} onClick={addParsingRow}><i className="ti ti-plus" /> Add</button>
                <button type="button" className="ca-btn" disabled={ro || selMaps.size === 0}
                  onClick={() => { setDraft(d => ({ ...d, parsing: d.parsing.filter(p => !selMaps.has(p.uqID)) })); setSelMaps(new Set()); }}>
                  <i className="ti ti-trash" /> Remove</button>
              </div>
              <AppDataGrid<TemplateParsing> rows={draft.parsing} columns={legacyMapCols} getRowId={r => r.uqID}
                selectable={!ro} selectedIds={selMaps} onSelectionChange={setSelMaps} onCellCommit={commitLegacyMap}
                defaultPageSize={10} hideHeaderTools emptyText="No data to display" />
            </>
          )}

          {tab === 'mapping' && !legacyTabs && (
            <>
              <div className="stb-actions">
                <button type="button" className="ca-btn" disabled={ro || !book} onClick={() => { if (selection()) setPicker('map'); }}>
                  <i className="ti ti-arrow-bar-to-down" /> Map Field to Selected Cell</button>
                <button type="button" className="ca-btn" disabled={ro || selMaps.size === 0}
                  onClick={() => { setDraft(d => ({ ...d, parsing: d.parsing.filter(p => !selMaps.has(p.uqID)) })); setSelMaps(new Set()); }}>
                  <i className="ti ti-trash" /> Remove</button>
                <button type="button" className="ca-btn" disabled={ro || !book} onClick={takeWorkbookFormulas}
                  title="Copy each mapped cell's formula from the workbook into the mapping (the workbook formula is what Result Entry runs)">
                  <i className="ti ti-arrow-back-up-double" /> Take Formulas from Workbook</button>
                <span className="stb-hint">Select a cell on the sheet below, then map a field to it. Write = Result Entry saves the cell back.</span>
              </div>
              <AppDataGrid<TemplateParsing> rows={draft.parsing} columns={mapCols} getRowId={r => r.uqID}
                selectable={!ro} selectedIds={selMaps} onSelectionChange={setSelMaps} onCellCommit={commitMap}
                defaultPageSize={10} hideHeaderTools emptyText="No cell is mapped yet." />
            </>
          )}

          {/* Legacy Field Info (G5): inner tabs Template | Calibration Field; on each, the fields still
              AVAILABLE on the left and the template's chosen fields on the right. A single click (or
              Enter) on an available field adds it (owner request 2026-10-07); clicking a chosen field's name sends it back.
              Same rows as the Toolbox Field Info below — only the layout differs. */}
          {tab === 'fields' && legacyTabs && (
            <div className="stb-fi">
              <div className="stb-fieldtabs stb-fi-tabs" role="tablist" aria-label="Field set" data-unsaved-ignore>
                <button type="button" role="tab" aria-selected={fiTab === 'RawDataTable'}
                  className={fiTab === 'RawDataTable' ? 'on' : ''} onClick={() => { setFiTab('RawDataTable'); setSelFields(new Set()); }}>Template</button>
                <button type="button" role="tab" aria-selected={fiTab === 'CalibrationTable'}
                  className={fiTab === 'CalibrationTable' ? 'on' : ''} onClick={() => { setFiTab('CalibrationTable'); setSelFields(new Set()); }}>Calibration Field</button>
              </div>
              <div className="stb-fi-body">
                <div className="stb-fi-left">
                  <AppDataGrid<FieldOption> rows={fiAvailable} columns={FI_AVAILABLE_COLS} getRowId={r => r.id}
                    onRowClick={ro ? undefined : addFieldRow}
                    defaultPageSize={20} hideHeaderTools emptyText="No data to display" />
                </div>
                <div className="stb-fi-right">
                  {/* No Remove button and no tick column (owner request 2026-10-08): clicking a chosen
                      field sends it back to the left list, the mirror of clicking one there to add it.
                      The click is on the FieldName cell — the other cells open their own editor. */}
                  <AppDataGrid<TemplateField> rows={fiChosen} columns={legacyFieldCols} getRowId={r => r.fieldID}
                    onRowClick={ro ? undefined : removeFieldRow} onCellCommit={commitField}
                    defaultPageSize={20} hideHeaderTools initialSort={{ key: 'sort', dir: 'asc' }} emptyText="No data to display" />
                </div>
              </div>
            </div>
          )}

          {tab === 'fields' && !legacyTabs && (
            <>
              <div className="stb-actions">
                <button type="button" className="ca-btn" disabled={ro} onClick={() => setPicker('fields')}><i className="ti ti-plus" /> Add Field</button>
                <button type="button" className="ca-btn" disabled={ro || selFields.size === 0}
                  onClick={() => { setDraft(d => ({ ...d, fields: d.fields.filter(f => !selFields.has(f.fieldID)) })); setSelFields(new Set()); }}>
                  <i className="ti ti-trash" /> Remove</button>
                <span className="stb-hint">The batch-results grid and export columns.</span>
              </div>
              <AppDataGrid<TemplateField> rows={draft.fields} columns={fieldCols} getRowId={r => r.fieldID}
                selectable={!ro} selectedIds={selFields} onSelectionChange={setSelFields} onCellCommit={commitField}
                defaultPageSize={10} hideHeaderTools initialSort={{ key: 'sort', dir: 'asc' }} emptyText="No field column yet." />
            </>
          )}

          {/* Always mounted (hidden when not shown) so its unsaved rows survive switching tabs. */}
          <div hidden={tab !== 'outputs'}>
            <DataTransferReportingTab ref={outputsRef} templateId={id} formName={FORM_NAME}
              canWrite={canWrite} canDelete={perm.canDelete} onDirtyChange={setOutputsDirty}
              legacy={legacyTabs}
              dataSourceFields={legacyTabs ? rawSourceFields : dataSourceFields}
              calibrationFields={calSourceFields} />
          </div>

          {/* Legacy Mail Merge (G5): the four ranges as typed boxes — Header / CHeader on the first row,
              Detail / CDetail on the second. A range typed here is the same mail-merge band the sheet's
              "Set Header Band" / "Set Detail Band" set; an empty box clears it. */}
          {tab === 'mailmerge' && (
            <div className="stb-mm">
              <div className="sd-grid sd-grid--side sd-grid--square stb-mm-grid">
                {([['HEADERRANGE', 'Header Range'], ['CHEADER', 'CHeader Range'], ['DETAILRANGE', 'Detail Range'], ['CDETAIL', 'CDetail Range']] as const).map(([band, label]) => (
                  <div className="sd-row" key={band}>
                    <label className="sd-label" htmlFor={`stb-mm-${band}`}>{label}</label>
                    <input id={`stb-mm-${band}`} className={`sd-input${ro ? ' readonly' : ''}`} readOnly={ro} placeholder="e.g. A1:F2"
                      value={allBands.find(b => b.sheetIndex === bandSheet && b.name === band)?.range ?? ''}
                      onChange={e => setBand(band, e.target.value.replace(/\$/g, '').trim().toUpperCase() || null, bandSheet)} />
                  </div>
                ))}
                <div className="sd-row stb-g5-top">
                  <span className="sd-label">Mail Merge Mode</span>
                  <div className="stb-g5-stack" role="radiogroup" aria-label="Mail Merge Mode">
                    {MAIL_MERGE_MODES.map((label, i) => (
                      <label key={label}><input type="radio" name="stb-mm-mode" disabled={ro} checked={(h.mailMergeMode ?? 0) === i}
                        onChange={() => setHeader({ mailMergeMode: i })} /> {label}</label>
                    ))}
                  </div>
                </div>
                <div className="sd-row stb-g5-top">
                  <span className="sd-label">Document Orientation</span>
                  <div className="stb-g5-stack" role="radiogroup" aria-label="Document Orientation">
                    {DOCUMENT_ORIENTATIONS.map((label, i) => (
                      <label key={label}><input type="radio" name="stb-mm-orient" disabled={ro} checked={(h.documentOrientation ?? 0) === i}
                        onChange={() => setHeader({ documentOrientation: i })} /> {label}</label>
                    ))}
                  </div>
                </div>
                {info.sheets.length > 1 && (
                  <div className="sd-row">
                    <label className="sd-label" htmlFor="stb-mm-sheet">Sheet</label>
                    <select id="stb-mm-sheet" className="sd-input" value={bandSheet} onChange={e => setBandSheet(Number(e.target.value))} data-unsaved-ignore>
                      {info.sheets.map((s, i) => <option key={s} value={i + 1}>{s}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Legacy Data Source Mapping (G5): inner tabs Header · Detail · CHeader · CDetail, each a
              Remove bar over a two-column grid (CaptionMapping / PositionMapping on Header, ColumnMapping / ColumnIndex on Detail). Rows are not added here (G5 has no Add on this tab). Header and Detail are the
              template's report header / detail maps (the same rows the Toolbox tab edits). CHeader and
              CDetail are shown empty: this editor does not read or write the calibration maps. */}
          {tab === 'datasource' && (
            <div className="stb-fi">
              <div className="stb-fieldtabs stb-fi-tabs" role="tablist" aria-label="Mapping" data-unsaved-ignore>
                {DS_TABS.map(t => (
                  <button key={t} type="button" role="tab" aria-selected={dsTab === t} className={dsTab === t ? 'on' : ''}
                    onClick={() => setDsTab(t)}>{t}</button>
                ))}
              </div>
              <div className="stb-ds-body">
                {dsTab === 'Header' && (
                  <>
                    <div className="stb-actions">
                      <button type="button" className="ca-btn" disabled={ro || selHeaders.size === 0}
                        onClick={() => { setDraft(d => ({ ...d, headers: d.headers.filter(x => !selHeaders.has(x.uqID)) })); setSelHeaders(new Set()); }}>
                        <i className="ti ti-trash" /> Remove</button>
                    </div>
                    <AppDataGrid<TemplateHeaderMap> rows={draft.headers} columns={legacyHeaderCols} getRowId={r => r.uqID}
                      selectable={!ro} selectedIds={selHeaders} onSelectionChange={setSelHeaders} defaultPageSize={10} hideHeaderTools
                      onCellCommit={(row, col, v) => setDraft(d => ({ ...d, headers: d.headers.map(x => x.uqID !== row.uqID ? x
                        : { ...x, [col.field]: col.field === 'position' ? v.replace(/\$/g, '').toUpperCase() : v }) }))}
                      emptyText="No data to display" />
                  </>
                )}
                {dsTab === 'Detail' && (
                  <>
                    <div className="stb-actions">
                      <button type="button" className="ca-btn" disabled={ro || selDetails.size === 0}
                        onClick={() => { setDraft(d => ({ ...d, details: d.details.filter(x => !selDetails.has(x.uqID)) })); setSelDetails(new Set()); }}>
                        <i className="ti ti-trash" /> Remove</button>
                    </div>
                    <AppDataGrid<TemplateDetailMap> rows={draft.details} columns={legacyDetailCols} getRowId={r => r.uqID}
                      selectable={!ro} selectedIds={selDetails} onSelectionChange={setSelDetails} defaultPageSize={10} hideHeaderTools
                      onCellCommit={(row, col, v) => setDraft(d => ({ ...d, details: d.details.map(x => x.uqID !== row.uqID ? x
                        : col.field === 'columnIndex' ? { ...x, columnIndex: colIndex(v) } : { ...x, columnName: v }) }))}
                      emptyText="No data to display" />
                  </>
                )}
                {(dsTab === 'CHeader' || dsTab === 'CDetail') && (
                  <>
                    <div className="stb-actions">
                      <button type="button" className="ca-btn" disabled><i className="ti ti-trash" /> Remove</button>
                    </div>
                    <AppDataGrid<TemplateHeaderMap> rows={NO_ROWS} columns={dsTab === 'CDetail' ? NO_DETAIL_COLS : legacyHeaderCols} getRowId={r => r.uqID}
                      defaultPageSize={10} hideHeaderTools emptyText="No data to display" />
                  </>
                )}
              </div>
            </div>
          )}

          {tab === 'bands' && (
            <div className="stb-bands">
              <div className="stb-bands-col">
                <div className="stb-sub">Mail-merge bands
                  <select className="sd-input stb-band-sheet" value={bandSheet} onChange={e => setBandSheet(Number(e.target.value))} aria-label="Sheet">
                    {(info.sheets.length ? info.sheets : ['Sheet1']).map((s, i) => <option key={s} value={i + 1}>{s}</option>)}
                  </select>
                </div>
                <table className="stb-band-table">
                  <tbody>
                    {BAND_NAMES.map(n => {
                      const cur = allBands.find(b => b.sheetIndex === bandSheet && b.name === n)?.range;
                      const pending = bands.some(b => b.sheetIndex === bandSheet && b.name === n);
                      return (
                        <tr key={n}>
                          <th>{BAND_LABEL[n]}</th>
                          <td className={pending ? 'pending' : ''}>{cur ?? <span className="stb-muted">not set</span>}</td>
                          <td>
                            <button type="button" className="ca-btn" disabled={ro || !book} onClick={() => bandFromSelection(n)}>Use Selection</button>
                            <button type="button" className="ca-btn" disabled={ro || !cur} onClick={() => setBand(n, null, bandSheet)}>Clear</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="stb-hint">Header rows merge once; the Detail row repeats once per sample.</p>
              </div>
              <div className="stb-bands-col">
                <div className="stb-sub">Report header (caption ← cell)
                  <span>
                    <button type="button" className="ca-btn" disabled={ro || !book} onClick={() => void addReportHeader()}>Add from Selection</button>
                    <button type="button" className="ca-btn" disabled={ro || selHeaders.size === 0}
                      onClick={() => { setDraft(d => ({ ...d, headers: d.headers.filter(x => !selHeaders.has(x.uqID)) })); setSelHeaders(new Set()); }}>Remove</button>
                  </span>
                </div>
                <AppDataGrid<TemplateHeaderMap> rows={draft.headers} columns={headerCols} getRowId={r => r.uqID}
                  selectable={!ro} selectedIds={selHeaders} onSelectionChange={setSelHeaders} defaultPageSize={5} hideHeaderTools
                  onCellCommit={(row, col, v) => setDraft(d => ({ ...d, headers: d.headers.map(x => x.uqID !== row.uqID ? x
                    : { ...x, [col.field]: col.field === 'position' ? v.replace(/\$/g, '').toUpperCase() : v }) }))}
                  emptyText="None." />
                <div className="stb-sub">Report detail (column name ← sheet column)
                  <span>
                    <button type="button" className="ca-btn" disabled={ro || !book} onClick={() => void addReportDetail()}>Add from Selection</button>
                    <button type="button" className="ca-btn" disabled={ro || selDetails.size === 0}
                      onClick={() => { setDraft(d => ({ ...d, details: d.details.filter(x => !selDetails.has(x.uqID)) })); setSelDetails(new Set()); }}>Remove</button>
                  </span>
                </div>
                <AppDataGrid<TemplateDetailMap> rows={draft.details} columns={detailCols} getRowId={r => r.uqID}
                  selectable={!ro} selectedIds={selDetails} onSelectionChange={setSelDetails} defaultPageSize={5} hideHeaderTools
                  onCellCommit={(row, col, v) => setDraft(d => ({ ...d, details: d.details.map(x => x.uqID !== row.uqID ? x
                    : col.field === 'columnIndex' ? { ...x, columnIndex: colIndex(v) } : { ...x, columnName: v }) }))}
                  emptyText="None." />
              </div>
            </div>
          )}

          {tab === 'review' && (
            <ul className="stb-review">
              {review.length === 0 && <li className="ok"><i className="ti ti-circle-check" /> No problems found — Result Entry can run this template.</li>}
              {review.map((r, i) => (
                <li key={i} className={r.level}>
                  <i className={`ti ${r.level === 'error' ? 'ti-circle-x' : r.level === 'warning' ? 'ti-alert-triangle' : 'ti-info-circle'}`} />
                  <b>{r.area}</b> — {r.message}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Splitter (Windows builder): drag to give the tab panel or the sheet more room. */}
        {panelOpen && !legacyTabs && (
          <div className="stb-splitter" role="separator" aria-orientation="horizontal" aria-label="Resize the tab panel"
            title="Drag to resize · double-click to reset" onPointerDown={startPanelResize}
            onDoubleClick={() => setPanelHeight(PANEL_DEFAULT_H)} />
        )}
        {/* Legacy tabs: the sheet belongs to the SDMS tab only. On the other tabs it stays MOUNTED
            (its content, selection and unsaved edits must survive) but is clipped out of view at its
            own size, so the canvas never measures a zero box. */}
        <div className={`stb-sheet-clip${legacyTabs && !sheetTab ? ' stb-sheet-clip--off' : ''}${legacyTabs && sheetTab ? ' stb-sheet-clip--boxed' : ''}${legacyTabs && tab === 'mapping' ? ' stb-sheet-clip--nofields' : ''}`}
          aria-hidden={legacyTabs && !sheetTab ? true : undefined}>
        <div className="stb-sheet-row" ref={sheetRowRef} style={sheetRowHeight ? { height: sheetRowHeight } : undefined}>
          {book && !ro && (
            <>
            {!fieldListPinned && (
              /* Collapsed dock: the vertical tab on the left edge (Windows auto-hide). */
              <button type="button" className={`stb-dock-tab${fieldListPeek ? ' on' : ''}`}
                onMouseEnter={showPeek} onMouseLeave={hidePeekSoon} onClick={showPeek} onFocus={showPeek}
                aria-expanded={fieldListPeek} title="Field List">
                <span>Field List</span>
              </button>
            )}
            <aside className={`stb-fieldlist stb-fieldlist--dock${fieldListPinned ? '' : ' stb-fieldlist--autohide'}`}
              aria-label="Field List" hidden={!fieldListPinned && !fieldListPeek}
              onMouseEnter={fieldListPinned ? undefined : showPeek}
              onMouseLeave={fieldListPinned ? undefined : hidePeekSoon}
              onDragStart={() => { fieldDragRef.current = true; }}
              onDragEnd={() => { fieldDragRef.current = false; if (!fieldListPinned) hidePeekSoon(); }}>
              <div className="stb-float-title">
                {legacyTabs ? (
                  <span className="stb-fieldtabs" role="tablist" aria-label="Field list" data-unsaved-ignore>
                    <button type="button" role="tab" aria-selected={fieldTab === 'RawDataTable'}
                      className={fieldTab === 'RawDataTable' ? 'on' : ''} onClick={() => setFieldTab('RawDataTable')}>Raw Data</button>
                    <button type="button" role="tab" aria-selected={fieldTab === 'CalibrationTable'}
                      className={fieldTab === 'CalibrationTable' ? 'on' : ''} onClick={() => setFieldTab('CalibrationTable')}>Calibration</button>
                  </span>
                ) : <span>Field List</span>}
                <button type="button" className="stb-float-close"
                  onClick={() => setFieldListPinned(p => !p)} aria-pressed={fieldListPinned}
                  aria-label={fieldListPinned ? 'Unpin — auto-hide the field list on the left edge' : 'Pin — dock the field list on the left'}
                  title={fieldListPinned ? 'Unpin — auto-hide the field list on the left edge' : 'Pin — dock the field list on the left'}>
                  <i className={`ti ${fieldListPinned ? 'ti-pinned' : 'ti-pin'}`} /></button>
              </div>
              <div className="stb-fieldlist-search">
                <input type="text" className="sd-input" placeholder="Search fields…" value={fieldSearch}
                  onChange={e => setFieldSearch(e.target.value)} data-unsaved-ignore aria-label="Search fields" />
              </div>
              {legacyTabs && <div className="stb-fieldlist-head">FieldName</div>}
              <ul className="stb-fieldlist-items">
                {legacyTabs && fieldList.length === 0 && <li className="stb-fieldlist-empty">No data to display</li>}
                {fieldList.map(f => (
                  <li key={f.id} draggable tabIndex={0}
                    className={mappedFieldIds.has(f.id) ? 'mapped' : undefined}
                    title={`${f.synonymEN ?? f.fieldName}\nDrag onto a cell, or press Enter / double-click to put it in the selected cell.`}
                    onDragStart={e => { e.dataTransfer.setData(FIELD_DRAG_MIME, String(f.id)); e.dataTransfer.effectAllowed = 'copy'; }}
                    onDoubleClick={() => insertFieldAtSelection(f)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); insertFieldAtSelection(f); } }}>
                    <span className="stb-fieldlist-name">{f.fieldName}</span>
                    {f.synonymEN && f.synonymEN !== f.fieldName && <span className="stb-fieldlist-cap">{f.synonymEN}</span>}
                  </li>
                ))}
              </ul>
            </aside>
            </>
          )}
        <div className="xlwrap xluniver stb-sheet">
          {book ? (
            <Suspense fallback={<div className="xl-loading">Loading spreadsheet…</div>}>
              <TemplateSheetDesigner key={upload ? `u-${upload.name}` : `t-${id ?? 'new'}-${loadMs ?? 0}`} ref={sheetRef} buf={book}
                sheetNames={info.sheets} formulas={info.formulas} readOnly={ro}
                onReady={() => setSheetReady(true)} onFieldDropped={onFieldDropped} onConditionalFormat={onConditionalFormat} onDataValidation={onDataValidation}
                templateActions={templateActions} />
            </Suspense>
          ) : (
            <div className="stb-empty">
              <i className="ti ti-file-spreadsheet" />
              <p>This template has no workbook yet.</p>
              <p className="stb-hint">Upload an Excel layout, or {id ? 'save' : 'save the new template'} to start from a blank sheet —
                then type, format and drag fields onto it here (the builder writes <code>FIELD("…")</code> into the cells you map).</p>
              {canWrite && <button type="button" className="ca-btn ca-btn-solid" onClick={() => fileRef.current?.click()}><i className="ti ti-upload" /> Upload Workbook</button>}
            </div>
          )}
        </div>
        </div>
        </div>
      </div>

      <FieldPickerDialog open={picker === 'map'} title="Map Field to Selected Cell" fields={lookups?.fields ?? []}
        onPick={p => { const f = p[0]; if (f) mapField(f); }} onClose={() => setPicker(null)} />
      <FieldPickerDialog open={picker === 'fields'} title="Add Field Info Columns" multi fields={lookups?.fields ?? []}
        exclude={new Set(draft.fields.map(f => f.fieldID))}
        onPick={p => { setDraft(d => { let fields = d.fields; for (const f of p) fields = [...fields, newFieldRow(f, fields)]; return { ...d, fields }; }); setPicker(null); }}
        onClose={() => setPicker(null)} />
      <LinkTestDialog open={linkOpen} tests={lookups?.testMethods ?? []}
        linkedTestIds={new Set(draft.tests.map(t => t.testMethodID.toUpperCase()))}
        onPick={linkTest} onClose={() => setLinkOpen(false)} />
      <LegacyFieldNameDialog open={fieldNameRow != null} fields={lookups?.fields ?? []}
        currentId={draft.parsing.find(p => p.uqID === fieldNameRow)?.fieldID}
        onPick={pickFieldName} onClose={() => setFieldNameRow(null)} />
      <LegacyTestDialog open={testsOpen} tests={lookups?.testMethods ?? []} linked={draft.tests}
        onApply={rows => { setDraft(d => ({ ...d, tests: rows })); setSelTests(new Set()); setTestsOpen(false); }}
        onClose={() => setTestsOpen(false)} />
      <ConditionalFormatManager open={cfManager != null} newRuleOnly={cfManager?.newRuleOnly} sheetNames={info.sheets.length ? info.sheets : ['Sheet1']}
        rules={cfRules} selection={cfManager?.selection ?? null} readOnly={ro}
        onApply={r => { setCfRules(r); setCfManager(null); }} onClose={() => setCfManager(null)} />
      <DataValidationManager open={dvManager != null} newRuleOnly={dvManager?.newRuleOnly} sheetNames={info.sheets.length ? info.sheets : ['Sheet1']}
        rules={dvRules} selection={dvManager?.selection ?? null} readOnly={ro}
        onApply={r => { setDvRules(r); setDvManager(null); }} onClose={() => setDvManager(null)} />
    </div>
  );
}
